import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronDown, Menu, MessageCircle, X } from 'lucide-react'
import { AnimatePresence, MotionConfig, motion, useMotionValueEvent, useScroll } from 'motion/react'
import { useEffect, useState, type ReactNode } from 'react'
import { COZUMLER } from '../icerik/cozumler'
import { DURUM_ETIKETI, MODULLER } from '../icerik/moduller'
import { Logo, LogoMark } from './logo'
import {
  ButonHref,
  ButonLink,
  Container,
  DEMO_YOLU,
  PANEL_URL,
  Rozet,
  WHATSAPP_URL,
  cn,
} from './ui'

/** Kabuk: sabit üst menü, sayfa, alt bilgi. Rota değişince en üste kayar. */
export function Kabuk({ children }: { children: ReactNode }) {
  const yol = useRouterState({ select: (s) => s.location.pathname })
  const hash = useRouterState({ select: (s) => s.location.hash })
  useEffect(() => {
    if (hash) {
      document.getElementById(hash)?.scrollIntoView()
      return
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [yol, hash])
  return (
    <MotionConfig reducedMotion="user">
      <div className="flex min-h-dvh flex-col">
        <a
          href="#icerik"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-[var(--radius-control)] focus:bg-surface focus:px-3 focus:py-2 focus:ring-1 focus:ring-line"
        >
          İçeriğe geç
        </a>
        <UstMenu />
        <main id="icerik" className="flex-1">
          {children}
        </main>
        <AltBilgi />
        {WHATSAPP_URL && <WhatsAppDugmesi />}
      </div>
    </MotionConfig>
  )
}

const KAYNAKLAR = [
  {
    to: '/karsilastirma',
    ad: 'Karşılaştırma',
    aciklama: 'Excel, masaüstü program ve ajansla yan yana',
  },
  {
    to: '/gecis',
    ad: 'Geçiş ve veri taşıma',
    aciklama: 'Excel aktarımı ücretsiz; eski yazılımdan taşıma teklifle',
  },
  {
    to: '/entegrasyonlar',
    ad: 'Entegrasyonlar',
    aciklama: 'Ödeme, SMS, e-posta, canlı yayın, muhasebe',
  },
  { to: '/yol-haritasi', ad: 'Yol haritası', aciklama: 'Ne hazır, ne geliştiriliyor, ne sırada' },
  { to: '/sss', ad: 'Sık sorulan sorular', aciklama: 'Kurulum, deneme, veri, ödeme, destek' },
  { to: '/hakkimizda', ad: 'Hakkımızda', aciklama: 'Kimiz, neden yapıyoruz' },
]

function UstMenu() {
  const [acik, setAcik] = useState<string | null>(null)
  const [mobil, setMobil] = useState(false)
  const [kaydi, setKaydi] = useState(false)
  const { scrollY } = useScroll()
  useMotionValueEvent(scrollY, 'change', (y) => setKaydi(y > 12))
  const yol = useRouterState({ select: (s) => s.location.pathname })
  // Rota değişince açık menü kapanır. Efekt yerine çizim sırasında: bir kare
  // boyunca eski menünün yeni sayfanın üstünde durması istenmiyor.
  const [sonYol, setSonYol] = useState(yol)
  if (yol !== sonYol) {
    setSonYol(yol)
    setAcik(null)
    setMobil(false)
  }
  useEffect(() => {
    document.documentElement.style.overflow = mobil ? 'hidden' : ''
    return () => {
      document.documentElement.style.overflow = ''
    }
  }, [mobil])

  return (
    <header className="fixed inset-x-0 top-0 z-50" onMouseLeave={() => setAcik(null)}>
      <div
        className={cn(
          'transition-[background-color,box-shadow,backdrop-filter] duration-300',
          kaydi || acik || mobil
            ? 'bg-page/85 shadow-[0_1px_0_var(--ad-line)] backdrop-blur-xl'
            : 'bg-transparent',
        )}
      >
        <Container className="flex h-16 items-center justify-between gap-4 sm:h-[72px]">
          <Link to="/" aria-label="Akıllı Dernek ana sayfa" className="shrink-0">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Ana menü">
            <MenuDugmesi
              ad="Özellikler"
              acik={acik === 'ozellikler'}
              onClick={() => setAcik(acik === 'ozellikler' ? null : 'ozellikler')}
              onHover={() => setAcik('ozellikler')}
            />
            <MenuDugmesi
              ad="Çözümler"
              acik={acik === 'cozumler'}
              onClick={() => setAcik(acik === 'cozumler' ? null : 'cozumler')}
              onHover={() => setAcik('cozumler')}
            />
            <MenuBaglanti to="/fiyatlandirma" onHover={() => setAcik(null)}>
              Fiyatlandırma
            </MenuBaglanti>
            <MenuDugmesi
              ad="Kaynaklar"
              acik={acik === 'kaynaklar'}
              onClick={() => setAcik(acik === 'kaynaklar' ? null : 'kaynaklar')}
              onHover={() => setAcik('kaynaklar')}
            />
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <ButonHref href={PANEL_URL} variant="ghost" size="sm">
              Giriş yap
            </ButonHref>
            <ButonLink to={DEMO_YOLU} size="sm" ok>
              Demo isteyin
            </ButonLink>
          </div>

          <button
            type="button"
            className="grid size-10 place-items-center rounded-[var(--radius-control)] text-ink lg:hidden"
            aria-label={mobil ? 'Menüyü kapat' : 'Menüyü aç'}
            aria-expanded={mobil}
            onClick={() => setMobil((v) => !v)}
          >
            {mobil ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </Container>

        <AnimatePresence>
          {acik && (
            <motion.div
              key={acik}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
              className="hidden lg:block"
            >
              <Container className="pb-6">
                <div className="rounded-[var(--radius-panel)] bg-surface p-3 ring-soft">
                  {acik === 'ozellikler' && <OzelliklerMenusu />}
                  {acik === 'cozumler' && <CozumlerMenusu />}
                  {acik === 'kaynaklar' && <KaynaklarMenusu />}
                </div>
              </Container>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {mobil && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto bg-page px-5 pb-10 pt-4 lg:hidden"
          >
            <MobilMenu />
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

function MenuDugmesi({
  ad,
  acik,
  onClick,
  onHover,
}: {
  ad: string
  acik: boolean
  onClick: () => void
  onHover: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onHover}
      onFocus={onHover}
      aria-expanded={acik}
      className={cn(
        'inline-flex h-10 items-center gap-1 rounded-[var(--radius-control)] px-3 text-sm font-medium transition-colors',
        acik ? 'bg-subtle text-ink' : 'text-ink-2 hover:bg-subtle hover:text-ink',
      )}
    >
      {ad}
      <ChevronDown
        className={cn('size-3.5 transition-transform', acik && 'rotate-180')}
        aria-hidden
      />
    </button>
  )
}

function MenuBaglanti({
  to,
  children,
  onHover,
}: {
  to: string
  children: ReactNode
  onHover: () => void
}) {
  return (
    <Link
      to={to}
      onMouseEnter={onHover}
      className="inline-flex h-10 items-center rounded-[var(--radius-control)] px-3 text-sm font-medium text-ink-2 transition-colors hover:bg-subtle hover:text-ink"
      activeProps={{ className: 'text-ink' }}
    >
      {children}
    </Link>
  )
}

function MenuOgesi({
  to,
  baslik,
  aciklama,
  Ikon,
  rozet,
}: {
  to: string
  baslik: string
  aciklama: string
  Ikon?: React.ComponentType<{ className?: string }>
  rozet?: string | null
}) {
  return (
    <Link
      to={to}
      className="group flex items-start gap-3 rounded-[var(--radius-card)] p-3 transition-colors hover:bg-subtle"
    >
      {Ikon && (
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-[var(--radius-control)] bg-brand-subtle text-brand">
          <Ikon className="size-4" />
        </span>
      )}
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-medium text-ink">
          {baslik}
          {rozet && <Rozet tone="warning">{rozet}</Rozet>}
        </span>
        <span className="mt-0.5 block text-xs leading-snug text-ink-3">{aciklama}</span>
      </span>
    </Link>
  )
}

function OzelliklerMenusu() {
  const cekirdek = MODULLER.filter((m) => m.grup === 'cekirdek')
  const paket = MODULLER.filter((m) => m.grup === 'paket')
  const ust = MODULLER.filter((m) => m.grup === 'ust')
  return (
    <div className="grid grid-cols-[2fr_1fr] gap-3">
      <div>
        <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-ink-3">
          Çekirdek · her planda
        </p>
        <div className="grid grid-cols-2">
          {cekirdek.map((m) => (
            <MenuOgesi
              key={m.slug}
              to={`/ozellikler/${m.slug}`}
              baslik={m.ad}
              aciklama={m.kisa}
              Ikon={m.ikon}
              rozet={DURUM_ETIKETI[m.durum]}
            />
          ))}
        </div>
      </div>
      <div className="border-l border-line pl-3">
        <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-ink-3">
          Paketler · plana eklenir
        </p>
        {paket.map((m) => (
          <MenuOgesi
            key={m.slug}
            to={`/ozellikler/${m.slug}`}
            baslik={m.ad}
            aciklama={m.kisa}
            Ikon={m.ikon}
            rozet={DURUM_ETIKETI[m.durum]}
          />
        ))}
        <p className="px-3 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wider text-ink-3">
          Platform
        </p>
        {ust.map((m) => (
          <MenuOgesi
            key={m.slug}
            to={`/ozellikler/${m.slug}`}
            baslik={m.ad}
            aciklama={m.kisa}
            Ikon={m.ikon}
          />
        ))}
        <Link
          to="/ozellikler"
          className="mt-2 block px-3 py-2 text-sm font-medium text-brand hover:underline"
        >
          Bütün özellikler
        </Link>
      </div>
    </div>
  )
}

function CozumlerMenusu() {
  return (
    <div className="grid grid-cols-3">
      {COZUMLER.map((c) => (
        <MenuOgesi
          key={c.slug}
          to={`/cozumler/${c.slug}`}
          baslik={c.ad}
          aciklama={c.kisa}
          Ikon={c.ikon}
        />
      ))}
    </div>
  )
}

function KaynaklarMenusu() {
  return (
    <div className="grid grid-cols-3">
      {KAYNAKLAR.map((k) => (
        <MenuOgesi key={k.to} to={k.to} baslik={k.ad} aciklama={k.aciklama} />
      ))}
    </div>
  )
}

function MobilMenu() {
  const grup = (baslik: string, ogeler: Array<{ to: string; ad: string }>) => (
    <div className="border-t border-line py-4">
      <p className="pb-2 text-[11px] font-medium uppercase tracking-wider text-ink-3">{baslik}</p>
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {ogeler.map((o) => (
          <Link
            key={o.to}
            to={o.to}
            className="rounded-[var(--radius-control)] px-2 py-2 text-sm text-ink hover:bg-subtle"
          >
            {o.ad}
          </Link>
        ))}
      </div>
    </div>
  )
  return (
    <div className="mx-auto max-w-xl">
      <div className="flex gap-2 pb-4">
        <ButonLink to={DEMO_YOLU} className="flex-1" ok>
          Demo isteyin
        </ButonLink>
        <ButonHref href={PANEL_URL} variant="secondary" className="flex-1">
          Giriş yap
        </ButonHref>
      </div>
      {grup(
        'Özellikler',
        MODULLER.map((m) => ({ to: `/ozellikler/${m.slug}`, ad: m.ad })),
      )}
      {grup(
        'Çözümler',
        COZUMLER.map((c) => ({ to: `/cozumler/${c.slug}`, ad: c.ad })),
      )}
      {grup('Kaynaklar', [
        { to: '/fiyatlandirma', ad: 'Fiyatlandırma' },
        ...KAYNAKLAR.map((k) => ({ to: k.to, ad: k.ad })),
      ])}
    </div>
  )
}

function AltBilgi() {
  const sutun = (baslik: string, ogeler: Array<{ to?: string; href?: string; ad: string }>) => (
    <div>
      <p className="text-sm font-semibold text-ink">{baslik}</p>
      <ul className="mt-3 space-y-2">
        {ogeler.map((o) => (
          <li key={o.ad}>
            {o.to ? (
              <Link to={o.to} className="text-sm text-ink-2 hover:text-ink">
                {o.ad}
              </Link>
            ) : (
              <a href={o.href} className="text-sm text-ink-2 hover:text-ink" rel="noreferrer">
                {o.ad}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
  return (
    <footer className="mt-auto border-t border-line bg-surface">
      <Container className="grid gap-10 py-14 md:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
        <div className="max-w-xs">
          <Logo />
          <p className="mt-4 text-sm leading-relaxed text-ink-2">
            Derneğinizin üyeleri, aidatı, web sitesi, kongresi ve eğitimi tek yerde. Türkiye’de
            geliştiriliyor.
          </p>
        </div>
        {sutun('Ürün', [
          { to: '/ozellikler', ad: 'Özellikler' },
          { to: '/fiyatlandirma', ad: 'Fiyatlandırma' },
          { to: '/entegrasyonlar', ad: 'Entegrasyonlar' },
          { to: '/ozellikler/guvenlik-ve-kvkk', ad: 'Güvenlik' },
          { to: '/yol-haritasi', ad: 'Yol haritası' },
        ])}
        {sutun(
          'Çözümler',
          COZUMLER.map((c) => ({ to: `/cozumler/${c.slug}`, ad: c.ad })),
        )}
        {sutun('Kaynaklar', [
          { to: '/karsilastirma', ad: 'Karşılaştırma' },
          { to: '/gecis', ad: 'Geçiş ve veri taşıma' },
          { to: '/sss', ad: 'Sık sorulan sorular' },
          { href: PANEL_URL, ad: 'Panele giriş' },
        ])}
        {sutun('Şirket', [
          { to: '/hakkimizda', ad: 'Hakkımızda' },
          { to: '/iletisim', ad: 'İletişim' },
          { to: '/kvkk', ad: 'KVKK aydınlatma metni' },
          { to: '/gizlilik', ad: 'Gizlilik' },
          { to: '/kosullar', ad: 'Kullanım koşulları' },
        ])}
      </Container>
      <div className="border-t border-line">
        <Container className="flex flex-col gap-2 py-5 text-xs text-ink-3 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Akıllı Dernek. Bütün hakları saklıdır.</p>
          <p className="flex items-center gap-2">
            <LogoMark boyut={14} className="text-brand" />
            Sürüm {import.meta.env.VITE_APP_VERSION?.slice(0, 7) || 'dev'}
          </p>
        </Container>
      </div>
    </footer>
  )
}

function WhatsAppDugmesi() {
  return (
    <a
      href={WHATSAPP_URL}
      rel="noreferrer"
      target="_blank"
      aria-label="WhatsApp ile yazın"
      className="fixed bottom-5 right-5 z-40 grid size-12 place-items-center rounded-full bg-brand text-on-brand shadow-lg transition-transform hover:scale-105"
    >
      <MessageCircle className="size-5" />
    </a>
  )
}
